import { useRef, useSyncExternalStore } from 'react';

function getSnapshot(): FontFaceLoadStatus {
  if ('fonts' in document) {
    return document.fonts.status;
  }
  return 'loaded';
}

function getServerSnapshot(): FontFaceLoadStatus {
  return 'loaded';
}

function subscribe(callback: () => void): () => void {
  if ('fonts' in document) {
    document.fonts.onloading = callback;
    document.fonts.onloadingerror = callback;
    document.fonts.onloadingdone = callback;
  }

  return () => {
    if ('fonts' in document) {
      document.fonts.onloading = null;
      document.fonts.onloadingerror = null;
      document.fonts.onloadingdone = null;
    }
  };
}

/**
 * Hook that checks if the initially loaded fonts were updated.
 * Used to trigger remeasuring in the DataTable (autoWidth) and TextEllipsis once all fonts are loaded.
 *
 * @param callback - optionally pass a function to be executed once when the fonts were updated
 * @returns boolean indicating whether the fonts have finished loading
 * @internal
 */
export function useFontsUpdated(callback?: () => void): boolean {
  const prevLoaded = useRef('loading');
  const loading = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  if (prevLoaded.current === 'loading' && loading === 'loaded') {
    callback?.();
  }
  prevLoaded.current = loading;

  return loading === 'loaded';
}
